/**
 * Pop-up del modulo ENISA di un report: tutti i campi richiesti dal sito ENISA SRP, resi in modo
 * generico dallo schema versionato servito da srp-service. Precompila ciò che il report sa già
 * (senza mai sovrascrivere la bozza), valida obbligatori / max-char / visibilità condizionale,
 * salva la bozza sul report (salvataggio esplicito: ogni campo cambiato diventa una voce del log
 * audit) ed esporta il file JSON per l'estensione Chrome che compila il sito ENISA.
 */
import { ModalPortal } from '../../../components/ModalPortal.js';
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../api/client.js';
import { downloadJson } from '../vexReport.js';
import { useEnisaForm, useEnisaFormSchema, useSaveEnisaForm, fetchEnisaExport } from './api.js';
import { EnisaFieldInput } from './EnisaFieldInput.js';
import {
  groupRows,
  groups,
  initialValues,
  missingRequired,
  setInLevel,
  topFields,
  visibleFields,
} from './logic.js';
import type {
  EnisaFieldDef,
  EnisaFormSchema,
  EnisaFormValues,
  EnisaGroupDef,
  EnisaGroupRow,
  EnisaScalar,
} from './types.js';
import styles from './EnisaForm.module.css';

const GROUP_ADD_LABEL: Record<string, string> = {
  products: 'Aggiungi prodotto',
};

const errMsg = (err: unknown, fallback: string) =>
  err instanceof ApiError || err instanceof Error ? err.message : fallback;

export interface EnisaFormDialogProps {
  reportId: number;
  /** Abilita modifica e salvataggio (VULNERABILITY_ANALYSIS). Senza: sola lettura + export. */
  canEdit: boolean;
  onClose: () => void;
}

export function EnisaFormDialog({ reportId, canEdit, onClose }: EnisaFormDialogProps) {
  const schemaQ = useEnisaFormSchema(true);
  const formQ = useEnisaForm(reportId, true);
  // Incrementato da «Ricarica la bozza» (dopo un conflitto): l'editor riparte dai dati del server.
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <ModalPortal>
      <div
        className={styles.overlay}
        data-testid="enisa-form-dialog"
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.box}>
          {(schemaQ.isLoading || formQ.isLoading) && (
            <p className={styles.muted} data-testid="enisa-form-loading">
              Caricamento modulo ENISA…
            </p>
          )}
          {(schemaQ.isError || formQ.isError) && (
            <div>
              <p className={styles.error} data-testid="enisa-form-errore-caricamento">
                Impossibile caricare il modulo ENISA.
              </p>
              <button type="button" className={styles.btnSecondary} onClick={onClose}>
                Chiudi
              </button>
            </div>
          )}
          {schemaQ.data && formQ.data && (
            <EnisaFormEditor
              key={reloadKey}
              reportId={reportId}
              schema={schemaQ.data}
              draftValues={formQ.data.draft?.values}
              prefill={formQ.data.prefill}
              revision={formQ.data.draft?.revision ?? 0}
              updatedAt={formQ.data.draft?.updatedAt}
              updatedBy={formQ.data.draft?.updatedBy}
              draftSchemaVersion={formQ.data.draft?.schemaVersion}
              canEdit={canEdit}
              onClose={onClose}
              onReload={() => {
                void formQ.refetch().then(() => setReloadKey((k) => k + 1));
              }}
            />
          )}
        </div>
      </div>
    </ModalPortal>
  );
}

interface EditorProps {
  reportId: number;
  schema: EnisaFormSchema;
  draftValues: EnisaFormValues | undefined;
  prefill: EnisaFormValues;
  revision: number;
  updatedAt?: string;
  updatedBy?: string;
  draftSchemaVersion?: number;
  canEdit: boolean;
  onClose: () => void;
  onReload: () => void;
}

function EnisaFormEditor({
  reportId,
  schema,
  draftValues,
  prefill,
  revision,
  updatedAt,
  updatedBy,
  draftSchemaVersion,
  canEdit,
  onClose,
  onReload,
}: EditorProps) {
  const iniziali = useMemo(() => initialValues(draftValues, prefill), [draftValues, prefill]);
  const [values, setValues] = useState<EnisaFormValues>(iniziali.values);
  const [prefilled, setPrefilled] = useState<Set<string>>(iniziali.prefilled);
  const [dirty, setDirty] = useState(false);
  const [showRequired, setShowRequired] = useState(false);
  const [esito, setEsito] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [conflitto, setConflitto] = useState(false);
  const [confermaChiusura, setConfermaChiusura] = useState(false);
  const [esportando, setEsportando] = useState(false);
  const save = useSaveEnisaForm(reportId);
  // Revisione su cui si basa il prossimo salvataggio (aggiornata da ogni salvataggio riuscito).
  const [rev, setRev] = useState(revision);

  const mancanti = missingRequired(schema, values);
  // Precompilati mai salvati: vanno salvati (e tracciati) prima di finire nell'export.
  const daSalvare = dirty || rev === 0 || prefilled.size > 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') richiediChiusura();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  function tocca(path: string) {
    setDirty(true);
    setEsito(null);
    if (prefilled.has(path)) {
      const next = new Set(prefilled);
      next.delete(path);
      setPrefilled(next);
    }
  }

  function setTop(field: EnisaFieldDef, v: EnisaScalar) {
    tocca(field.id);
    setValues((cur) => setInLevel(topFields(schema), cur, field.id, v));
  }

  function setRow(group: EnisaGroupDef, index: number, field: EnisaFieldDef, v: EnisaScalar) {
    tocca(`${group.id}[${index}].${field.id}`);
    setValues((cur) => {
      const rows = groupRows(group, cur).map((r, i) =>
        i === index ? setInLevel(group.fields, r, field.id, v) : r,
      );
      return { ...cur, [group.id]: rows };
    });
  }

  function addRow(group: EnisaGroupDef) {
    setDirty(true);
    setValues((cur) => ({ ...cur, [group.id]: [...groupRows(group, cur), {}] }));
  }

  function removeRow(group: EnisaGroupDef, index: number) {
    setDirty(true);
    setValues((cur) => ({
      ...cur,
      [group.id]: groupRows(group, cur).filter((_, i) => i !== index),
    }));
    // I percorsi delle righe successive scalano: i badge «precompilato» del gruppo decadono.
    setPrefilled((cur) => new Set([...cur].filter((p) => !p.startsWith(`${group.id}[`))));
  }

  async function salva(): Promise<boolean> {
    setErrore(null);
    setEsito(null);
    setShowRequired(true);
    try {
      const res = await save.mutateAsync({ values, revision: rev });
      setRev(res.draft.revision);
      setEsito(
        res.changes === 0
          ? 'Nessuna modifica da salvare.'
          : `Bozza salvata (revisione ${res.draft.revision}): ${res.changes} ${
              res.changes === 1 ? 'campo registrato' : 'campi registrati'
            } nel log audit.`,
      );
      setDirty(false);
      setPrefilled(new Set());
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setConflitto(true);
      setErrore(errMsg(err, 'Errore durante il salvataggio'));
      return false;
    }
  }

  async function esporta() {
    setErrore(null);
    if (canEdit && daSalvare && !(await salva())) return;
    setEsportando(true);
    try {
      const doc = await fetchEnisaExport(reportId);
      downloadJson(doc, `enisa-report-${reportId}-r${doc.draftRevision}.json`);
    } catch (err) {
      setErrore(errMsg(err, 'Errore durante l’export'));
    } finally {
      setEsportando(false);
    }
  }

  function richiediChiusura() {
    if (dirty && canEdit) setConfermaChiusura(true);
    else onClose();
  }

  const disabled = !canEdit || save.isPending;

  const renderLevel = (
    fields: EnisaFieldDef[],
    level: Record<string, EnisaScalar>,
    prefix: string,
    onSet: (f: EnisaFieldDef, v: EnisaScalar) => void,
  ) =>
    visibleFields(fields, level).map(({ field, depth }) => {
      const path = `${prefix}${field.id}`;
      return (
        <EnisaFieldInput
          key={path}
          field={field}
          path={path}
          value={level[field.id]}
          onChange={(v) => onSet(field, v)}
          showRequired={showRequired}
          prefilled={prefilled.has(path)}
          disabled={disabled}
          depth={depth}
        />
      );
    });

  const topLevel = values as Record<string, EnisaScalar>;
  const gruppi = new Map(groups(schema).map((g) => [g.id, g]));

  return (
    <>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Modulo ENISA — Report #{reportId}</h2>
          <p className={styles.muted} data-testid="enisa-form-stato">
            {rev > 0
              ? `Bozza rev. ${rev}, salvata ${
                  updatedAt ? new Date(updatedAt).toLocaleString('it-IT') : ''
                }${updatedBy ? ` da ${updatedBy}` : ''}`
              : 'Bozza mai salvata: i campi evidenziati sono precompilati dal report.'}{' '}
            · schema v{schema.schemaVersion}
          </p>
          {draftSchemaVersion !== undefined && draftSchemaVersion !== schema.schemaVersion && (
            <p className={styles.warning}>
              La bozza è stata salvata con lo schema v{draftSchemaVersion}: ricontrolla i campi.
            </p>
          )}
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Chiudi"
          onClick={richiediChiusura}
          data-testid="enisa-form-chiudi"
        >
          ×
        </button>
      </header>

      <p className={styles.intro}>
        Campi del sito ENISA (Single Reporting Platform), con le etichette originali. Compilali qui
        una volta sola: «Esporta JSON» scarica il file per l’estensione Chrome che compila il sito
        ENISA. Ogni campo modificato è registrato nel log audit al salvataggio.
        {!canEdit && ' Sola lettura: serve il permesso di analisi per modificare.'}
      </p>

      <div className={styles.body}>
        {schema.nodes.map((node) => {
          if (node.kind === 'field') {
            return (
              <div key={node.id}>{renderLevel([node], topLevel, '', (f, v) => setTop(f, v))}</div>
            );
          }
          const group = gruppi.get(node.id)!;
          const rows = groupRows(group, values);
          return (
            <fieldset key={node.id} className={styles.group} data-testid={`enisa-group-${node.id}`}>
              <legend className={styles.groupLegend}>{node.label}</legend>
              {rows.map((row: EnisaGroupRow, i) => (
                <div
                  key={i}
                  className={styles.groupRow}
                  data-testid={`enisa-group-${node.id}-${i}`}
                >
                  {rows.length > 1 && (
                    <div className={styles.groupRowHead}>
                      <span className={styles.muted}>
                        {node.label} #{i + 1}
                      </span>
                      {rows.length > group.minItems && (
                        <button
                          type="button"
                          className={styles.linkBtn}
                          disabled={disabled}
                          onClick={() => removeRow(group, i)}
                          data-testid={`enisa-group-${node.id}-${i}-rimuovi`}
                        >
                          Rimuovi
                        </button>
                      )}
                    </div>
                  )}
                  {renderLevel(group.fields, row, `${node.id}[${i}].`, (f, v) =>
                    setRow(group, i, f, v),
                  )}
                </div>
              ))}
              <button
                type="button"
                className={styles.linkBtn}
                disabled={disabled}
                onClick={() => addRow(group)}
                data-testid={`enisa-group-${node.id}-aggiungi`}
              >
                + {GROUP_ADD_LABEL[node.id] ?? 'Aggiungi riga'}
              </button>
            </fieldset>
          );
        })}
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerInfo}>
          {mancanti.length > 0 ? (
            <span className={styles.warning} data-testid="enisa-form-mancanti">
              {mancanti.length} {mancanti.length === 1 ? 'campo obbligatorio' : 'campi obbligatori'}{' '}
              da compilare: la bozza si salva comunque, l’export è marcato incompleto.
            </span>
          ) : (
            <span className={styles.ok} data-testid="enisa-form-completo">
              Tutti i campi obbligatori sono compilati.
            </span>
          )}
          {esito && (
            <span className={styles.ok} data-testid="enisa-form-esito" role="status">
              {esito}
            </span>
          )}
          {errore && (
            <span className={styles.error} data-testid="enisa-form-errore" role="alert">
              {errore}
              {conflitto && (
                <>
                  {' '}
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={onReload}
                    data-testid="enisa-form-ricarica"
                  >
                    Ricarica la bozza
                  </button>
                </>
              )}
            </span>
          )}
        </div>
        <div className={styles.footerActions}>
          {confermaChiusura ? (
            <>
              <span className={styles.warning}>Modifiche non salvate: chiudere comunque?</span>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setConfermaChiusura(false)}
              >
                Continua a modificare
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={onClose}
                data-testid="enisa-form-chiudi-conferma"
              >
                Chiudi senza salvare
              </button>
            </>
          ) : (
            <>
              <button type="button" className={styles.btnSecondary} onClick={richiediChiusura}>
                Chiudi
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={esportando || save.isPending || (!canEdit && rev === 0)}
                onClick={() => void esporta()}
                title={
                  !canEdit && rev === 0
                    ? 'Nessuna bozza salvata da esportare'
                    : 'Salva le modifiche e scarica il file JSON per l’estensione Chrome'
                }
                data-testid="enisa-form-esporta"
              >
                {esportando ? 'Esportazione…' : 'Esporta JSON'}
              </button>
              {canEdit && (
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={save.isPending}
                  onClick={() => void salva()}
                  data-testid="enisa-form-salva"
                >
                  {save.isPending ? 'Salvataggio…' : 'Salva bozza'}
                </button>
              )}
            </>
          )}
        </div>
      </footer>
    </>
  );
}
